import { animate, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  Inject,
  Injector,
  input,
  Optional,
  output,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { TiptapEditorOutputFormat } from '@examdojo/core/tiptap';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import type { SlashMenuItem } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import type { TiptapSmartLinkConfig } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { isExternalUrl } from '@examdojo/core/util/url-utils';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { distinctUntilDeepChanged } from '@examdojo/rxjs';
import {
  SMART_LINK_CONFIG,
  SmartLinkConfig,
  SmartLinkInputComponent,
  toReactSmartLinkConfig,
} from '@examdojo/smart-link';
import { TranslocoPipe } from '@jsverse/transloco';
import type { Editor, JSONContent } from '@tiptap/core';
import { combineLatest, debounceTime, distinctUntilChanged, filter, merge, tap } from 'rxjs';

@Component({
  selector: 'dojo-popover-content',
  imports: [
    ButtonComponent,
    IconButtonComponent,
    SmartLinkInputComponent,
    TranslocoPipe,
    RouterLink,
    TiptapEditorWrapperComponent,
    ReactiveFormsModule,
    NgTemplateOutlet,
  ],
  templateUrl: './popover-content.component.html',
  styleUrls: ['./popover-content.component.scss'],
  animations: [
    trigger('panelAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [animate('100ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' }))]),
    ]),
  ],
  host: {
    '[@panelAnimation]': 'true',
    class: 'block',
  },
})
export class PopoverContentComponent {
  constructor(
    private readonly injector: Injector,
    private readonly errorHandlerService: ErrorHandlerService,
    @Optional() @Inject(SMART_LINK_CONFIG) private readonly smartLinkConfig: SmartLinkConfig | null,
  ) {
    merge(this.syncInputs(), this.syncChanges()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly jsonContent = input.required<string>();
  readonly linkUrl = input<string>();
  readonly isPinned = input<boolean>(false);
  readonly allowEditing = input<boolean>(false);
  readonly editorOptions = input<Partial<Editor['options']>>();
  readonly slashMenuItems = input<Record<string, SlashMenuItem>>();

  readonly isEditing = signal(false);
  readonly isExternalLink = computed(() => {
    const url = this.linkUrl();
    return !!url && isExternalUrl(url);
  });
  readonly hasResolver = !!this.smartLinkConfig?.resolve;
  readonly editorControl = new FormControl<JSONContent>({}, { nonNullable: true });
  readonly linkUrlControl = new FormControl<string>('', { nonNullable: true });

  readonly closePopover = output<void>();
  readonly contentChanged = output<string>();
  readonly linkUrlChanged = output<string>();

  private readonly jsonContent$ = toObservable(this.jsonContent);
  private readonly linkUrl$ = toObservable(this.linkUrl);
  private readonly isPinned$ = toObservable(this.isPinned);
  private readonly allowEditing$ = toObservable(this.allowEditing);

  readonly TiptapEditorOutputFormat = TiptapEditorOutputFormat;

  readonly reactSmartLinkConfig = computed<TiptapSmartLinkConfig | undefined>(() => {
    const smartLinkConfig = this.smartLinkConfig;
    if (!smartLinkConfig) {
      return undefined;
    }

    return runInInjectionContext(this.injector, () => toReactSmartLinkConfig(smartLinkConfig));
  });

  onClose(): void {
    this.closePopover.emit();
  }

  onResolveLink(): void {
    const url = this.linkUrl();
    if (url && this.smartLinkConfig) {
      this.smartLinkConfig.resolve(url, { url: window.location.href });
      this.onClose();
    }
  }

  private syncInputs() {
    const syncJsonContent$ = this.jsonContent$.pipe(
      filter(() => !this.isEditing()),
      tap((content) => {
        // Parse JSON string to object for Tiptap editor
        try {
          const parsed = JSON.parse(content);
          // Guard against null/undefined to prevent Tiptap from receiving invalid content
          if (parsed !== null && parsed !== undefined) {
            this.editorControl.setValue(parsed, { emitEvent: false });
          }
        } catch (error) {
          this.errorHandlerService.error('[PopoverContentComponent.syncJsonContent] Failed to parse JSON content', {
            context: { content },
            err: error,
          });
        }
      }),
    );

    const syncLinkUrl$ = this.linkUrl$.pipe(
      filter(() => !this.isEditing()),
      tap((url) => this.linkUrlControl.setValue(url ?? '', { emitEvent: false })),
    );

    const syncEditingState$ = combineLatest([this.isPinned$, this.allowEditing$]).pipe(
      tap(([isPinned, allowEditing]) => this.isEditing.set(isPinned && allowEditing)),
    );

    return merge(syncJsonContent$, syncLinkUrl$, syncEditingState$);
  }

  private syncChanges() {
    const contentChanges$ = this.editorControl.valueChanges.pipe(
      filter(() => this.isEditing()),
      debounceTime(300),
      distinctUntilDeepChanged(),
      tap((value) => {
        // Stringify JSON content for emission
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        this.contentChanged.emit(stringValue);
      }),
    );

    const linkUrlChanges$ = this.linkUrlControl.valueChanges.pipe(
      filter(() => this.isEditing()),
      debounceTime(300),
      distinctUntilChanged(),
      tap((value) => {
        this.linkUrlChanged.emit(value);
      }),
    );

    return merge(contentChanges$, linkUrlChanges$);
  }
}
